import { css } from '@emotion/react';
import {
  faEdit, faLoader,
} from '@fortawesome/pro-solid-svg-icons';
import {
  type FC, type PropsWithChildren, type ReactNode, useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { IconButtonComponent } from '~/_shared/baseComponents/buttons';
import { ButtonComponent } from '~/_shared/baseComponents/buttons/button/button.component';
import {
  ButtonSize, ButtonStyle,
} from '~/_shared/baseComponents/buttons/button/button.types';
import { spinAnimationStyle } from '~/_shared/baseComponents/cssAnimations/spin.animation';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { Inline } from '~/_shared/baseComponents/layout/inline.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import { OneDimensionalTableWithHeader } from '~/_shared/baseComponents/oneDimensionalTable/oneDimensionalTableWithHeader';
import {
  RadioGroupComponent, type RadioGroupItem,
} from '~/_shared/baseComponents/radio/radioGroup.component';
import { RectangleTag } from '~/_shared/baseComponents/rectangleTag';
import { TinyMapColorTileComponent } from '~/_shared/baseComponents/tinyMapPlaceholder';
import {
  TooltipBehavior, TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import {
  hexAndOpacityToRGBA, rgbColorToString,
} from '~/_shared/components/colorPicker/colorPicker.helpers';
import { ModalComponent } from '~/_shared/components/modal/modal.component';
import { OverlayLoaderComponent } from '~/_shared/components/overlay/overlayLoader.component';
import { useTheme } from '~/_shared/themes/theme.hooks';
import type { Theme } from '~/_shared/themes/theme.model';
import { createUuid } from '~/_shared/utils/createUuid';
import { useTranslation } from '~/_shared/utils/hooks';
import { getOverlappingBoundaries } from '~/boundary/editMode/boundaryEditSubmitModal/boundaryEditSubmit.utils';
import { BoundaryEditSubmitModalSelectBoundaryComponent } from '~/boundary/editMode/boundaryEditSubmitModal/boundaryEditSubmitModalSelectBoundary.component';
import { type ModalProps } from '~/modal/modalType.enum';
import { type BoundaryCombineGeometryRequestItem } from '~/store/boundaries/boundaries.repository';
import { displayNameComparer } from '~/store/boundaryItems/boundaryItems.helpers';
import { type MapBoundaryAreaQueryBoundaryResponse } from '~/store/boundaryItems/boundaryItems.repository';
import { type BoundaryStateItem } from '~/store/boundaryItems/boundaryItems.state';
import { type BoundaryTerritoryGroup } from '~/store/boundaryTerritoryGroups/boundaryTerritoryGroups.state';
import { useClientIdSelector } from '~/store/selectors/useClientIdSelector';
import { useMapIdSelector } from '~/store/selectors/useMapIdSelector';
import { Trans } from '~/translations/Trans';
import type { Translations } from '~/translations/translations.type';
import { type BoundaryEditManualMethod } from '../boundaryEditModal/boundaryEditModal.helpers';
import { BoundaryEditSubmitModalAction } from './boundaryEditSubmitModalAction.enum';

export type BoundaryEditSubmitComponentProps = {
  isLoading: boolean;
  onSubmit: (results: BoundaryEditSubmitOption, editAnother: boolean) => void;
  boundaries: ReadonlyArray<BoundaryStateItem>;
  method: BoundaryEditManualMethod;
  selectedBoundaryId?: number;
  editedBoundaryTerritoryGroup: BoundaryTerritoryGroup;
  geometry: BoundaryCombineGeometryRequestItem[];
  submitAction?: BoundaryEditSubmitModalAction;
  hideEditAnother: boolean;
  isBoundarySelectDisabled: boolean;
  onSelectBoundaryFromMapClick: (action: BoundaryEditSubmitModalAction | null) => void;
};

type BoundaryEditSubmitModalProps = ModalProps<BoundaryEditSubmitComponentProps>;

type BoundaryEditOptionRemoveBoundariesFromGroup = {
  action: BoundaryEditSubmitModalAction.RemoveBoundariesFromTerritory;
  selectedBoundaryId: number;
};

type BoundaryEditOptionAddToExistingTerritory = {
  action: BoundaryEditSubmitModalAction.AddToExistingTerritory;
  selectedBoundaryId: number;
};

type BoundaryEditOptionReassignTerritory = {
  action: BoundaryEditSubmitModalAction.ReassignTerritory;
  targetBoundaryId: number;
};

export type BoundaryEditSubmitOption =
  BoundaryEditOptionRemoveBoundariesFromGroup |
  BoundaryEditOptionAddToExistingTerritory |
  BoundaryEditOptionReassignTerritory;

const panelDropdownSectionStyle = css({
  marginBottom: 8,
});

const labelStyle = css({
  fontSize: '14px',
  marginBottom: '4px',
});

const submitButtonStyles = css({
  height: 40,
});

const contentStyle = css({
  overflow: 'hidden',
});

const modalContentStyle = css({
  padding: '15px 20px 10px',
  fontWeight: 400,
});

const radioGroupStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 16,
});

const uppercaseStyle = css({
  textTransform: 'uppercase',
});

const editedBoundaryNameWrapperStyle = css({
  alignItems: 'center',
  whiteSpace: 'nowrap',
  maxWidth: '100%',
  minWidth: 0,
  fontSize: '14px',
});

const selectedBoundaryNameStyle = (theme: Theme) => css({
  color: theme.textColors.info,
});

const ellipsisStyle = css({
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
});

const promptStyle = css({
  margin: 0,
  fontSize: 16,
  fontWeight: 400,
});

const radioButtonLabelStyle = css({
  flexGrow: 1,
});

const radioButtonSubsectionWrapperStyle = css({
  paddingLeft: 28,
  marginTop: 4,
});

const radioButtonSubsectionExplanationStyle = (theme: Theme) => css({
  fontSize: 12,
  color: theme.textColors.secondary,
});

const radioButtonSubsectionButtonTextStyle = css({
  alignItems: 'center',
});

const radioButtonSubsectionButtonStyle = css({
  marginRight: 'auto',
});

const afterTagStyle = (theme: Theme) => css({
  color: theme.alertColors.successPrimary,
  fontSize: 10,
});

const reassignDisabledExplanationStyle = (theme: Theme) => css({
  color: theme.alertColors.warningPrimary,
  fontSize: 12,
});

const smallColoredTagStyle = css({
  borderWidth: 1,
  borderStyle: 'solid',
  fontSize: 10,
  padding: 2,
});

const oneDimensionalTableStyleOverride = (theme: Theme) => css({
  '[data-element="one-dimensional-table-header"]': {
    color: theme.textColors.secondary,
    fontSize: 12,
  },
});

const EditedBoundaryName = ({ children }: PropsWithChildren) => {
  return (
    <TooltipComponent
      tooltipContent={children}
      placement={TooltipPlacement.Top}
      behavior={TooltipBehavior.ShowOnOverflow}
    >
      <span css={[selectedBoundaryNameStyle, ellipsisStyle]}>
        {children}
      </span>
    </TooltipComponent>
  );
};

const RadioButtonLabel = ({ labelTranslationKey, boundaryName, suffixElement }: Readonly<{
  labelTranslationKey: keyof Translations;
  boundaryName?: string | null;
  suffixElement?: ReactNode;
}>) => {
  return (
    <Inline
      gap={12}
      wrap="nowrap"
      css={editedBoundaryNameWrapperStyle}
    >
      <Inline
        gap={4}
        wrap="nowrap"
        css={editedBoundaryNameWrapperStyle}
      >
        <Trans
          i18nKey={labelTranslationKey}
          values={{ boundaryName: boundaryName ?? '' }}
          components={{
            s: <span />,
            bn: <EditedBoundaryName />,
          }}
        />
      </Inline>
      {suffixElement}
    </Inline>
  );
};

const SmallColoredTag = ({
  color,
  children,
}: PropsWithChildren<{ color: string }>) => {
  return (
    <RectangleTag
      style={{
        color,
        backgroundColor: rgbColorToString(hexAndOpacityToRGBA(color, 0.1)),
        borderColor: color,
      }}
      css={smallColoredTagStyle}
    >
      {children}
    </RectangleTag>
  );
};

type OverlappingBoundariesProps = Readonly<{
  overlappingBoundaries: MapBoundaryAreaQueryBoundaryResponse[];
  selectedBoundary: BoundaryStateItem | null;
  action: BoundaryEditSubmitModalAction.AddToExistingTerritory | BoundaryEditSubmitModalAction.ReassignTerritory;
}>;

const OverlappingBoundaries = ({
  overlappingBoundaries,
  selectedBoundary,
  action,
}: OverlappingBoundariesProps) => {
  const [t] = useTranslation();
  const boundariesToShow = overlappingBoundaries.filter((boundary) => boundary.boundary_id !== selectedBoundary?.id);

  if (boundariesToShow.length === 0) {
    return (
      <OneDimensionalTableWithHeader
        css={oneDimensionalTableStyleOverride}
        heading={t('boundaryEdit.overlappingBoundaries.noOverlaps')}
        body={{ rows: [] }}
      />
    );
  }

  if (action === BoundaryEditSubmitModalAction.AddToExistingTerritory) {
    return (
      <OneDimensionalTableWithHeader
        css={oneDimensionalTableStyleOverride}
        heading={t('boundaryEdit.add.overlappingBoundaries.tableHeading')}
        body={{
          rows: boundariesToShow.map((boundary) => ({
            id: boundary.boundary_id,
            name: boundary.display_name,
          })),
        }}
      />
    );
  }

  if (action === BoundaryEditSubmitModalAction.ReassignTerritory) {
    return (
      <OneDimensionalTableWithHeader
        css={oneDimensionalTableStyleOverride}
        heading={t('boundaryEdit.reassign.overlappingBoundaries.tableHeading')}
        body={{
          rows: boundariesToShow.map((boundary) => ({
            id: boundary.boundary_id,
            name: boundary.display_name,
          })),
        }}
      />
    );
  }

  return null;
};

const getMapMiniature = (color: string, lineColor: string, lineWidth: number) => (
  <TinyMapColorTileComponent
    color={hexAndOpacityToRGBA(color, 1)}
    lineColor={hexAndOpacityToRGBA(lineColor, 1)}
    lineWidth={Math.ceil(lineWidth / 2)}
    size="Small"
  />
);

type BoundaryEditSubmitState = {
  action: BoundaryEditSubmitModalAction | null;
  setAction: (action: BoundaryEditSubmitModalAction | null) => void;
  selectedBoundaryId: number | null;
  setSelectedBoundaryId: (id: number | null) => void;
  isListingOverlaps: boolean;
  overlappingBoundaries: MapBoundaryAreaQueryBoundaryResponse[] | null;
  loadOverlaps: () => Promise<void>;
  isSubmitDisabled: boolean;
  handleSubmit: (editAnother: boolean) => void;
};

export const useBoundaryEditSubmitState = (props: BoundaryEditSubmitComponentProps): BoundaryEditSubmitState => {
  const mapId = useMapIdSelector();
  const clientId = useClientIdSelector();
  const [action, setAction] = useState<BoundaryEditSubmitModalAction | null>(props.submitAction ?? null);
  const [selectedBoundaryId, setSelectedBoundaryId] = useState<number | null>(props.selectedBoundaryId ?? null);
  const [isListingOverlaps, setIsListingOverlaps] = useState(false);
  const [overlappingBoundaries, setOverlappingBoundaries] = useState<MapBoundaryAreaQueryBoundaryResponse[] | null>(null);
  const overlappingBoundariesQueryIdRef = useRef<string | null>(null);

  const isSubmitDisabled =
    props.isLoading ||
    action === null ||
    (action === BoundaryEditSubmitModalAction.AddToExistingTerritory && selectedBoundaryId === null) ||
    (action === BoundaryEditSubmitModalAction.RemoveBoundariesFromTerritory && selectedBoundaryId === null) ||
    (action === BoundaryEditSubmitModalAction.ReassignTerritory && selectedBoundaryId === null);

  const handleSubmit = (editAnother: boolean) => {
    switch (action) {
      case BoundaryEditSubmitModalAction.RemoveBoundariesFromTerritory: {
        if (selectedBoundaryId === null) {
          break;
        }

        props.onSubmit({
          action: BoundaryEditSubmitModalAction.RemoveBoundariesFromTerritory,
          selectedBoundaryId,
        }, editAnother);
        break;
      }

      case BoundaryEditSubmitModalAction.AddToExistingTerritory: {
        if (selectedBoundaryId === null) {
          break;
        }

        props.onSubmit({
          action: BoundaryEditSubmitModalAction.AddToExistingTerritory,
          selectedBoundaryId,
        }, editAnother);
        break;
      }

      case BoundaryEditSubmitModalAction.ReassignTerritory: {
        if (selectedBoundaryId === null) {
          return;
        }

        props.onSubmit({
          action: BoundaryEditSubmitModalAction.ReassignTerritory,
          targetBoundaryId: selectedBoundaryId,
        }, editAnother);
        break;
      }

      default: {
        return;
      }
    }
  };

  const loadOverlaps = useCallback(async () => {
    if (clientId && mapId) {
      setIsListingOverlaps(true);
      try {
        const queryId = createUuid();
        overlappingBoundariesQueryIdRef.current = queryId;

        const areaQueryResponse = await getOverlappingBoundaries(
          clientId,
          mapId,
          props.geometry,
          props.editedBoundaryTerritoryGroup.boundaryGroupId,
        );

        if (overlappingBoundariesQueryIdRef.current === queryId) {
          setOverlappingBoundaries(areaQueryResponse.boundaries);
        }
      }
      catch {
        console.error('Server returned unexpected response');
        setIsListingOverlaps(false);
        return;
      }
    }
  }, [clientId, mapId, props.editedBoundaryTerritoryGroup.boundaryGroupId, props.geometry]);

  useEffect(() => {
    overlappingBoundariesQueryIdRef.current = null;
    setIsListingOverlaps(false);
    setOverlappingBoundaries(null);
  }, [loadOverlaps]);

  return {
    action,
    setAction,
    selectedBoundaryId,
    setSelectedBoundaryId,
    isListingOverlaps,
    overlappingBoundaries,
    loadOverlaps,
    isSubmitDisabled,
    handleSubmit,
  };
};

type BoundaryEditSubmitBodyProps = BoundaryEditSubmitComponentProps & {
  state: BoundaryEditSubmitState;
};

export const BoundaryEditSubmitBody: FC<BoundaryEditSubmitBodyProps> = ({ state, ...props }) => {
  const [t] = useTranslation();
  const theme = useTheme();
  const { onSelectBoundaryFromMapClick } = props;
  const {
    action,
    setAction,
    selectedBoundaryId,
    setSelectedBoundaryId,
    isListingOverlaps,
    overlappingBoundaries,
    loadOverlaps,
  } = state;

  const selectedBoundary = useMemo(() =>
    props.boundaries.find(boundary => boundary.id === selectedBoundaryId) ?? null,
  [props.boundaries, selectedBoundaryId],
  );
  const [isSelectingBoundary, setIsSelectingBoundary] = useState(!selectedBoundary);

  const selectedBoundaryName: string | null = props.boundaries.find(item => item.id === selectedBoundaryId)?.displayName ?? null;
  const sameBoundaryGroupId = (props.geometry?.[0]?.type === 'boundary') && (props.geometry?.[0]?.boundary_group_id === props.editedBoundaryTerritoryGroup.boundaryGroupId);
  const allowReassign = props.boundaries.length >= 2 && !sameBoundaryGroupId;

  const startBoundaryChoice = useCallback(() => {
    setIsSelectingBoundary(true);
  }, []);

  const selectBoundaryFromMap = useCallback(() => {
    onSelectBoundaryFromMapClick(action);
  }, [action, onSelectBoundaryFromMapClick]);

  const territorySelectOptions = useMemo(() =>
    [...props.boundaries]
      .sort(displayNameComparer)
      .map(boundary => {
        const lineColor = props.editedBoundaryTerritoryGroup.settings.style.lineColor;
        const lineWidth = props.editedBoundaryTerritoryGroup.settings.style.lineWidth;
        const fillColor = props.editedBoundaryTerritoryGroup.settings.boundaryStyle.get(boundary.id);
        const prefix = fillColor ? getMapMiniature(fillColor.color, lineColor, lineWidth) : undefined;
        return ({
          name: boundary.displayName,
          value: boundary.id,
          prefix,
        });
      }),
  [props.boundaries, props.editedBoundaryTerritoryGroup],
  );

  const boundarySelectionSection = () => {
    return (
      <div css={panelDropdownSectionStyle}>
        <div css={labelStyle}>
          {t('boundaryEdit.selectTerritory')}
        </div>

        <BoundaryEditSubmitModalSelectBoundaryComponent
          placeholder={t('boundaryEdit.selectTerritory')}
          isDisabled={false}
          onGoToMapClick={selectBoundaryFromMap}
          selectedBoundaryId={selectedBoundaryId}
          onSelectedBoundaryIdChange={setSelectedBoundaryId}
          selectedBoundaryIdOptions={territorySelectOptions}
          selectedBoundaryName={selectedBoundaryName}
        />
      </div>
    );
  };

  const radioGroupItems: RadioGroupItem<BoundaryEditSubmitModalAction>[] = [
    {
      value: BoundaryEditSubmitModalAction.AddToExistingTerritory,
      isDisabled: !selectedBoundary,
      tooltip: selectedBoundary ? undefined : t('boundaryEdit.radioButton.noEditedTerritory.tooltip'),
      label: (
        <RadioButtonLabel
          labelTranslationKey="boundaryEdit.add.label"
          boundaryName={selectedBoundary?.displayName}
          suffixElement={(
            <>
              <SmallColoredTag color={theme.alertColors.successPrimary}>
                {t('boundaryEdit.add.tag')}
              </SmallColoredTag>
              <span css={afterTagStyle}>
                {t('boundaryEdit.add.afterTag')}
              </span>
            </>
          )}
        />
      ),
      children: (
        <Stack gap={12} css={radioButtonSubsectionWrapperStyle}>
          <div css={radioButtonSubsectionExplanationStyle}>
            {t('boundaryEdit.add.explanation')}
          </div>
          {(action === BoundaryEditSubmitModalAction.AddToExistingTerritory) && (
            overlappingBoundaries ? (
              <OverlappingBoundaries
                overlappingBoundaries={overlappingBoundaries}
                selectedBoundary={selectedBoundary}
                action={action}
              />
            ) : (
              <ButtonComponent
                onClick={loadOverlaps}
                isDisabled={isListingOverlaps}
                css={radioButtonSubsectionButtonStyle}
                size={ButtonSize.Small}
                buttonStyle={ButtonStyle.Quaternary}
                text={(
                  <Inline gap={4} css={radioButtonSubsectionButtonTextStyle}>
                    <span>
                      {t('boundaryEdit.listOverlaps.button')}
                    </span>
                    {isListingOverlaps && (
                      <FontAwesomeIcon icon={faLoader} css={spinAnimationStyle} />
                    )}
                  </Inline>
                )}
              />
            )
          )}
        </Stack>
      ),
    },
    {
      value: BoundaryEditSubmitModalAction.RemoveBoundariesFromTerritory,
      isDisabled: !selectedBoundary,
      tooltip: selectedBoundary ? undefined : t('boundaryEdit.radioButton.noEditedTerritory.tooltip'),
      label: (
        <RadioButtonLabel
          labelTranslationKey="boundaryEdit.remove.label"
          boundaryName={selectedBoundary?.displayName}
          suffixElement={(
            <SmallColoredTag color={theme.alertColors.dangerPrimary}>
              {t('boundaryEdit.remove.tag')}
            </SmallColoredTag>
          )}
        />
      ),
      children: (
        <Stack gap={12} css={radioButtonSubsectionWrapperStyle}>
          <div css={radioButtonSubsectionExplanationStyle}>
            {t('boundaryEdit.remove.explanation')}
          </div>
        </Stack>
      ),
    },
    {
      value: BoundaryEditSubmitModalAction.ReassignTerritory,
      isDisabled: !selectedBoundary || !allowReassign,
      tooltip: selectedBoundary ? undefined : t('boundaryEdit.radioButton.noEditedTerritory.tooltip'),
      label: (
        <RadioButtonLabel
          labelTranslationKey="boundaryEdit.reassign.label"
          boundaryName={selectedBoundary?.displayName}
          suffixElement={(
            <SmallColoredTag color={theme.alertColors.warningPrimary}>
              {t('boundaryEdit.reassign.tag')}
            </SmallColoredTag>
          )}
        />
      ),
      children: (
        <Stack gap={12} css={radioButtonSubsectionWrapperStyle}>
          {allowReassign ? (
            <div css={radioButtonSubsectionExplanationStyle}>
              {t('boundaryEdit.reassign.explanation')}
            </div>
          ) : (
            <div css={reassignDisabledExplanationStyle}>
              {t('boundaryEdit.reassign.disabled.explanation')}
            </div>
          )}
          {(action === BoundaryEditSubmitModalAction.ReassignTerritory) && (
            overlappingBoundaries ? (
              <OverlappingBoundaries
                overlappingBoundaries={overlappingBoundaries}
                selectedBoundary={selectedBoundary}
                action={action}
              />
            ) : (
              <ButtonComponent
                onClick={loadOverlaps}
                isDisabled={isListingOverlaps}
                css={radioButtonSubsectionButtonStyle}
                size={ButtonSize.Small}
                buttonStyle={ButtonStyle.Quaternary}
                text={(
                  <Inline gap={4} css={radioButtonSubsectionButtonTextStyle}>
                    <span>
                      {t('boundaryEdit.listOverlaps.button')}
                    </span>
                    {isListingOverlaps && (
                      <FontAwesomeIcon icon={faLoader} css={spinAnimationStyle} />
                    )}
                  </Inline>
                )}
              />
            )
          )}
        </Stack>
      ),
    },
  ];

  return (
    <div css={contentStyle}>
      <Stack gap={32}>
        {isSelectingBoundary ? boundarySelectionSection() : (
          <Inline
            wrap="nowrap"
            gap={8}
            css={[uppercaseStyle, editedBoundaryNameWrapperStyle]}
          >
            <Trans
              i18nKey="boundaryEdit.editingBoundary.name"
              values={{ boundaryName: selectedBoundary?.displayName ?? '' }}
              components={{
                s: <span />,
                bn: <EditedBoundaryName />,
              }}
            />
            {!props.isBoundarySelectDisabled && (
              <IconButtonComponent
                icon={faEdit}
                tooltipLabel={t('Edit')}
                tooltipPlacement={TooltipPlacement.Top}
                onClick={startBoundaryChoice}
                size="s"
              />
            )}
          </Inline>
        )}
        <Stack gap={16}>
          <h3 css={promptStyle}>
            {t('boundaryEdit.selectedAreas.prompt')}
          </h3>
          <RadioGroupComponent<BoundaryEditSubmitModalAction>
            onValueChange={setAction}
            selectedValue={action}
            items={radioGroupItems}
            css={radioGroupStyle}
            labelStyle={radioButtonLabelStyle}
          />
        </Stack>
      </Stack>
    </div>
  );
};

export const BoundaryEditSubmitModalComponent: FC<BoundaryEditSubmitModalProps> = ({
  isOpen,
  onClose,
  ...props
}) => {
  const [t] = useTranslation();
  const state = useBoundaryEditSubmitState(props);

  return (
    <ModalComponent
      caption={t('Territory Editing')}
      isOpen={isOpen}
      onClose={onClose}
      contentStyle={modalContentStyle}
      leftFooterContent={props.hideEditAnother ? null : (
        <ButtonComponent
          buttonStyle={ButtonStyle.Secondary}
          isDisabled={state.isSubmitDisabled}
          onClick={() => state.handleSubmit(true)}
          text={t('Save and Keep Editing')}
        />
      )}
      confirmButton={(
        <ButtonComponent
          css={submitButtonStyles}
          text={t('Done')}
          onClick={() => state.handleSubmit(false)}
          isDisabled={state.isSubmitDisabled}
        />
      )}
      additionalContent={props.isLoading ? <OverlayLoaderComponent /> : null}
    >
      <BoundaryEditSubmitBody {...props} state={state} />
    </ModalComponent>
  );
};
